import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import apiClient from '../api/api';
import { useAuth } from '../context/AuthContext';
import { 
    FaMapMarkerAlt, FaMoneyBillWave, FaGavel, FaClock, FaCalendarAlt, 
    FaUserTag, FaSearch, FaFilter, FaArrowRight, FaCheckCircle 
} from 'react-icons/fa';
import CountdownTimer from '../components/CountdownTimer';

function JobBoardPage() {
  const [view, setView] = useState('all'); // 'all', 'direct', 'bidding'
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    const fetchJobs = async () => {
      setLoading(true);
      setError('');
      try {
        // Fetch ALL jobs to allow client-side filtering comfortably
        const response = await apiClient.get(`/jobs?limit=100`);
        setJobs(response.data.jobs || []);
        setFilteredJobs(response.data.jobs || []);
      } catch (err) {
        setError('Failed to load jobs. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchJobs();
  }, []);

  // Filter Logic
  useEffect(() => {
      const now = new Date();
      let result = jobs.filter(job => {
          // 1. Status Check
          if (job.status !== 'open' && job.status !== 'bidding') return false;

          // 2. Deadline/Time Check
          if (job.post_model === 'bidding') {
              const session = job.details?.[0]?.bidding_sessions?.[0];
              if (session?.end_time && new Date(session.end_time) < now) return false;
          } else if (job.post_model === 'direct_apply') {
              const hire = job.direct_hires?.[0];
              if (hire?.Application_Deadline) {
                  const deadline = new Date(hire.Application_Deadline);
                  // Set deadline to end of that day to be inclusive
                  deadline.setHours(23, 59, 59, 999);
                  if (deadline < now) return false;
              }
          }
          return true;
      });

      // Type Filter
      if (view === 'direct') result = result.filter(j => j.post_model === 'direct_apply');
      if (view === 'bidding') result = result.filter(j => j.post_model === 'bidding');

      // Search Filter
      if (searchTerm) {
          const lower = searchTerm.toLowerCase();
          result = result.filter(j => 
              j.title.toLowerCase().includes(lower) || 
              j.description.toLowerCase().includes(lower) ||
              j.location?.address?.toLowerCase().includes(lower)
          );
      }

      setFilteredJobs(result);
  }, [view, searchTerm, jobs]);

  const handleViewDetails = (jobId) => {
    navigate(`/job/${jobId}`);
  };

  const JobCard = ({ job }) => (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.2 }}
      onClick={() => handleViewDetails(job.id)}
      className="group bg-white rounded-[2rem] p-6 border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all cursor-pointer relative overflow-hidden flex flex-col h-full"
    >
        {/* Hover Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-slate-50 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"></div>

        {/* Top Badge & Date */}
        <div className="flex justify-between items-start mb-4 relative z-10">
            <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${
                job.post_model === 'bidding' ? 'bg-purple-100 text-purple-700' : 'bg-emerald-100 text-emerald-700'
            }`}>
                {job.post_model === 'bidding' ? 'Bidding' : 'Direct Hire'}
            </span>
            <span className="text-xs font-bold text-slate-400">
                {new Date(job.createdAt).toLocaleDateString()}
            </span>
        </div>

        {/* Title & Poster */}
        <div className="mb-6 relative z-10">
            <h3 className="text-lg font-black text-slate-800 mb-2 line-clamp-2 leading-tight group-hover:text-emerald-700 transition-colors">
                {job.title}
            </h3>
            <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-500">
                    {job.poster?.name?.charAt(0) || 'U'}
                </div>
                <span className="text-xs font-bold text-slate-500">{job.poster?.name || 'Unknown'}</span>
            </div>
        </div>

        {/* Info Grid */}
        <div className="grid grid-cols-2 gap-3 mb-6 relative z-10">
            <div className="bg-slate-50 rounded-xl p-3">
                <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Budget</p>
                <p className="text-sm font-black text-slate-800">৳{job.details?.[0]?.budget_per_worker}</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-3">
                <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Location</p>
                <p className="text-xs font-bold text-slate-700 truncate">{typeof job.location === 'object' ? job.location.address?.split(',')[0] : job.location}</p>
            </div>
        </div>

        {/* Footer */}
        <div className="mt-auto flex items-center justify-between pt-4 border-t border-slate-50 relative z-10">
            {job.post_model === 'bidding' ? (
                <div className="flex items-center text-xs font-bold text-purple-600">
                    <FaClock className="mr-1" />
                    <CountdownTimer targetDate={job.details?.[0]?.bidding_sessions?.[0]?.end_time} />
                </div>
            ) : (
                <div className="flex items-center text-xs font-bold text-emerald-600">
                    <FaCheckCircle className="mr-1" /> Active
                </div>
            )}
            
            <button className="w-8 h-8 rounded-full bg-slate-900 text-white flex items-center justify-center group-hover:bg-emerald-600 transition-colors">
                <FaArrowRight size={12} />
            </button>
        </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-slate-50/50 pt-20 pb-20 font-sans">
      
      {/* Header Section */}
      <div className="bg-slate-900 text-white pt-16 pb-24 -mt-20 mb-[-60px] relative overflow-hidden">
          <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
          <div className="container mx-auto px-6 text-center relative z-10">
              <h1 className="text-4xl md:text-5xl font-black mb-4 tracking-tight">Find Your Next Job</h1>
              <p className="text-slate-400 text-lg max-w-2xl mx-auto">Browse verified opportunities from top employers.</p>
          </div>
      </div>

      <div className="container mx-auto px-6 max-w-7xl relative z-20">
          
          {/* Search & Filter Bar */}
          <div className="bg-white p-6 rounded-[2rem] shadow-xl shadow-slate-200/50 border border-slate-100 mb-10 flex flex-col gap-6">
              <div className="relative w-full">
                  <FaSearch className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input 
                      type="text" 
                      placeholder="Search jobs by title or location..." 
                      className="w-full pl-12 pr-4 py-4 rounded-2xl bg-slate-50 border-none outline-none font-medium text-slate-700 focus:ring-2 focus:ring-emerald-500 transition-all"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                  />
              </div>
              <div className="flex flex-wrap gap-2 w-full justify-start">
                  <button 
                      onClick={() => setView('all')}
                      className={`px-6 py-3 rounded-2xl font-bold text-sm whitespace-nowrap transition-all flex-1 sm:flex-none ${view === 'all' ? 'bg-slate-900 text-white shadow-lg' : 'bg-slate-50 text-slate-500 hover:bg-slate-100'}`}
                  >
                      All Jobs
                  </button>
                  <button 
                      onClick={() => setView('direct')}
                      className={`px-6 py-3 rounded-2xl font-bold text-sm whitespace-nowrap transition-all flex-1 sm:flex-none ${view === 'direct' ? 'bg-emerald-600 text-white shadow-lg' : 'bg-slate-50 text-slate-500 hover:bg-slate-100'}`}
                  >
                      Direct Hire
                  </button>
                  <button 
                      onClick={() => setView('bidding')}
                      className={`px-6 py-3 rounded-2xl font-bold text-sm whitespace-nowrap transition-all flex-1 sm:flex-none ${view === 'bidding' ? 'bg-purple-600 text-white shadow-lg' : 'bg-slate-50 text-slate-500 hover:bg-slate-100'}`}
                  >
                      Bidding
                  </button>
              </div>
          </div>

          {/* Content */}
          {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 animate-pulse">
                  {[1,2,3,4,5,6,7,8].map(i => <div key={i} className="h-80 bg-slate-200 rounded-[2rem]"></div>)}
              </div>
          ) : filteredJobs.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-[3rem] border border-dashed border-slate-200">
                  <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-300">
                      <FaSearch className="text-3xl" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-700">No jobs match your criteria</h3>
                  <button onClick={() => {setSearchTerm(''); setView('all');}} className="mt-4 text-emerald-600 font-bold hover:underline">Clear Filters</button>
              </div>
          ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  <AnimatePresence>
                      {filteredJobs.map(job => <JobCard key={job.id} job={job} />)}
                  </AnimatePresence>
              </div>
          )}

          {!isAuthenticated && (
            <div className="mt-20 text-center">
                <p className="text-slate-400 font-medium mb-4">Want to apply to these jobs?</p>
                <Link to="/register" className="inline-block bg-slate-900 text-white px-8 py-3 rounded-xl font-bold hover:bg-slate-800 transition-all shadow-lg">Create Worker Account</Link>
            </div>
          )}
      </div>
    </div>
  );
}

// Helper icons needed if not imported
// Adding FaCheckCircle to imports above

export default JobBoardPage;
